import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import swal from "sweetalert";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import ErrorBoundary from "../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import { truncateMessage } from "../../utils/marketingTemplateUtils";
import {
  getThemeGradientButtonStyle,
  onThemeGradientButtonEnter,
  onThemeGradientButtonLeave,
} from "../../utils/themeGradientButtonStyle";
import "./manageEmailPresets.scss";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";

const PRESET_SORT_FIELDS = {
  presetId: "EmailPreset_ID",
  presetTitle: "PresetTitle",
  subject: "Subject",
  createdAt: "CreatedDateTime",
};

const PRESET_SORT_LABELS = {
  [PRESET_SORT_FIELDS.presetId]: "Preset ID",
  [PRESET_SORT_FIELDS.presetTitle]: "Preset Title",
  [PRESET_SORT_FIELDS.subject]: "Subject",
  [PRESET_SORT_FIELDS.createdAt]: "Created Date",
};

const normalizePreset = (row) => ({
  emailPreset_ID: row.emailPreset_ID ?? row.EmailPreset_ID,
  emailPreset_Guid: row.emailPreset_Guid ?? row.EmailPreset_Guid ?? "",
  presetTitle: row.presetTitle ?? row.PresetTitle ?? "",
  subject: row.subject ?? row.Subject ?? "",
  htmlBody: row.htmlBody ?? row.HtmlBody ?? "",
  heroImageUrl: row.heroImageUrl ?? row.HeroImageUrl ?? "",
  createdDateTime: row.createdDateTime ?? row.CreatedDateTime,
});

const formatPresetId = (id) => {
  const num = Number(id);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return `#${String(num).padStart(5, "0")}`;
};

const formatCreatedAt = (dateVal) => {
  if (!dateVal) return "—";
  try {
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const ManageEmailPresets = () => {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "emailPresets";
  const defaultVisibleColumns = {
    presetId: true,
    presetTitle: true,
    subject: true,
    createdAt: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = {
    value: PRESET_SORT_FIELDS.createdAt,
    label: PRESET_SORT_LABELS[PRESET_SORT_FIELDS.createdAt],
  };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize && validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const presetsFetchSeqRef = useRef(0);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: PRESET_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const hasFiltersApplied = () => search.trim() !== "";

  const clearFilters = () => {
    setSearch("");
    setPageNumber(1);
  };

  const fetchPresets = useCallback(async () => {
    const fetchSeq = ++presetsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_EMAIL_PRESETS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          PageNumber: pageNumber,
          PageSize: Number(pageSize.value),
        },
      });

      if (fetchSeq !== presetsFetchSeqRef.current) return;

      const raw = response.data?.data ?? [];
      const list = (Array.isArray(raw) ? raw : []).map(normalizePreset);
      setPresets(list);
      const total = response.data?.totalRecords ?? list.length;
      setTotalRecords(total);
      setTotalPages(
        response.data?.totalPages ?? Math.max(1, Math.ceil(total / Number(pageSize.value)))
      );

      if (!hasFiltersApplied()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, total);
      }
    } catch {
      if (fetchSeq !== presetsFetchSeqRef.current) return;
      setPresets([]);
      setTotalRecords(0);
      showErrorToast("Failed to load email presets.");
    } finally {
      if (fetchSeq === presetsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy.value, sortOrder.value, pageNumber, pageSize.value]);

  const handleDelete = async (preset) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Delete email preset "${preset.presetTitle}"?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirmDelete) return;

    const presetGuid = preset.emailPreset_Guid ?? preset.EmailPreset_Guid;
    if (!presetGuid) {
      showErrorToast("Email preset identifier is missing.");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_EMAIL_PRESET(presetGuid),
        { params: { CompID } }
      );
      if (response.data?.status) {
        showSuccessToast("Email preset deleted successfully.");
        fetchPresets();
      } else {
        showErrorToast(response.data?.message || "Delete failed.");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      dispatch(
        setVisibleColumns({
          pageKey,
          columns: mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns),
        })
      );
    }
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(presetsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchPresets(), [fetchPresets]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value, pageNumber]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const isUnfilteredView = !hasFiltersApplied();
  const showEmptyCatalogState =
    initialLoadComplete && !loading && isUnfilteredView && catalogTotalRecords === 0;
  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No email presets match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no email presets yet. Create a preset to use in email marketing campaigns.";
  };

  const renderNoDataMessage = () => {
    if (loading || presets.length > 0) return null;
    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No email presets found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const gradientBtnStyle = getThemeGradientButtonStyle(themeColor);

  return (
    <ErrorBoundary>
      <div>
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item">
                <Link to="/marketing/manageMarketingGroups">Marketing</Link>
              </li>
              <li className="breadcrumb-item active">Email Preset</li>
            </ol>
            <h1 className="page-header mb-0">Email Preset</h1>
          </div>

          <div className="ms-auto">
            <button
              type="button"
              onClick={() => navigate("/marketing/addEmailPreset")}
              className="btn px-4"
              style={gradientBtnStyle}
              onMouseEnter={(e) => onThemeGradientButtonEnter(e, themeColor)}
              onMouseLeave={(e) => onThemeGradientButtonLeave(e, themeColor)}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
              Create New Preset
            </button>
          </div>
        </div>

        <div className="mb-3 mt-2">
          {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

          {showEmptyCatalogState && (
            <AddButton
              onClick={() => navigate("/marketing/addEmailPreset")}
              title="Create New Preset"
            />
          )}

          {showGridToolbar && (
            <ModernGridToolbar
              isDarkMode={isDarkMode}
              title="Email Preset"
              totalCount={totalRecords}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Preset title, subject, or body"
              bodyFlush
              headerControls={
                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                />
              }
            >
              <div className="px-3 pb-3">
                <GridSectionLoader loading={loading} minHeight="280px">
                  {renderNoDataMessage()}

                  {!loading && presets.length > 0 && (
                    <div
                      className="table-responsive table-container"
                      style={{
                        minHeight: "200px",
                        maxHeight: "450px",
                        overflowY: "auto",
                        overflowX: "auto",
                      }}
                    >
                      <table
                        className="table table-hover manage-email-presets-table"
                        style={{ "--modern-grid-sort-active": themeColor }}
                      >
                        <thead
                          className="bg-light sticky-header"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 10000,
                            backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                            color: isDarkMode ? "#ffffff" : "#000000",
                          }}
                        >
                          <tr>
                            {visibleColumns.presetId && (
                              <SortableColumnHeader
                                sortField={PRESET_SORT_FIELDS.presetId}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Preset ID
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.presetTitle && (
                              <SortableColumnHeader
                                sortField={PRESET_SORT_FIELDS.presetTitle}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Preset Title
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.subject && (
                              <SortableColumnHeader
                                sortField={PRESET_SORT_FIELDS.subject}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Subject
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.createdAt && (
                              <SortableColumnHeader
                                sortField={PRESET_SORT_FIELDS.createdAt}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Created Date
                              </SortableColumnHeader>
                            )}
                            <th className="pt-2 pb-2 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {presets.map((preset) => (
                            <tr
                              key={preset.emailPreset_ID}
                              onClick={() =>
                                navigate(
                                  `/marketing/updateEmailPreset/${preset.emailPreset_ID}`
                                )
                              }
                              style={{ cursor: "pointer" }}
                            >
                              {visibleColumns.presetId && (
                                <td className="align-middle text-muted">
                                  {formatPresetId(preset.emailPreset_ID)}
                                </td>
                              )}
                              {visibleColumns.presetTitle && (
                                <td className="align-middle fw-semibold">
                                  {preset.presetTitle}
                                </td>
                              )}
                              {visibleColumns.subject && (
                                <td
                                  className="align-middle manage-email-presets-table__subject"
                                  title={preset.subject}
                                >
                                  {truncateMessage(preset.subject, 80)}
                                </td>
                              )}
                              {visibleColumns.createdAt && (
                                <td className="align-middle">
                                  {formatCreatedAt(preset.createdDateTime)}
                                </td>
                              )}
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <TableActionsMenu menuEnd menuStyle={{ zIndex: 1050 }}>
                                  <TableActionsItem
                                    as={Link}
                                    to={`/marketing/updateEmailPreset/${preset.emailPreset_ID}`}
                                    icon="fa fa-edit"
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => handleDelete(preset)}
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </GridSectionLoader>
              </div>
            </ModernGridToolbar>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ManageEmailPresets;
